import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { Can, DATE_FORMATS_FNS, ReactTable } from "components";
import { useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import "modules/ParkingGateCodesDashboard/components/ParkingGateCodesTable/ParkingGateCodesTable.scss";
import { useParkingGateCodesQuery } from "modules/ParkingGateCodesDashboard/hooks";
import { formatDate } from "utils/dateHelperFunctions";

import type { ParkingGateCode } from "models";

const columnHelper = createColumnHelper<ParkingGateCode>();

type ParkingGateCodeTableProps = {
  onDeleteParkingGateCode: (id: number) => void;
  onUpdateParkingGateCode: (data: ParkingGateCode) => void;
};

export const ParkingGateCodesTable = ({ onDeleteParkingGateCode, onUpdateParkingGateCode }: ParkingGateCodeTableProps) => {
  const { t } = useTranslation();

  const { data: parkingGateCodes, isFetching, isLoading, refetch: refetchParkingGateCodes } = useParkingGateCodesQuery();
  useRegisterNavAction(refetchParkingGateCodes);

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "start_at",
        header: () => <span>{t("v8_start_at", "Start At")}</span>,
        size: 230,
        cell: ({ row: { original } }) => <span> {formatDate(original.start_at, DATE_FORMATS_FNS.dateMonthYear)} </span>
      }),
      columnHelper.accessor(row => row, {
        id: "end_at",
        header: () => <span>{t("v8_end_at", "End At")}</span>,
        size: 230,
        cell: ({ row: { original } }) => <span> {formatDate(original.end_at, DATE_FORMATS_FNS.dateMonthYear)} </span>
      }),
      columnHelper.accessor(row => row, {
        id: "pin",
        header: () => <span>{t("v8_pin", "PIN").toUpperCase()}</span>,
        size: 800,
        cell: ({ row: { original } }) => <span> {original.pin} </span>
      }),

      columnHelper.accessor(row => row, {
        id: "actions",
        size: 50,
        header: () => null,
        cell: ({ row: { original } }) => (
          <div className="actions-container">
            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.ParkingGateCode}>
              <div className="action-edit-button-container" onClick={() => onUpdateParkingGateCode(original)}>
                <Icon className="icon pen to square edit-icon action-button-icon-center" color="#32bf53" />
              </div>
            </Can>
            <Can I={ACL_ACTIVITIES.Delete} the={ACL_RESOURCES.ParkingGateCode}>
              <div className="action-button-container" onClick={() => onDeleteParkingGateCode(original.id)}>
                <Icon className="trash action-button-icon-center" color="red" />
              </div>
            </Can>
          </div>
        )
      })
    ],
    [t]
  );

  return (
    <div className="ParkingGateCodesTable">
      <ReactTable columns={columns} data={parkingGateCodes} isFetching={isFetching || isLoading} />
    </div>
  );
};
